// TODO: To be remove, issue: https://github.com/tannerlinsley/react-table/issues/3106
import React from 'react';

import {
  actions,
  ensurePluginOrder,
  makePropGetter,
  useGetLatest,
  useMountedLayoutEffect
} from 'react-table';

const pluginName = 'useRowSelect';

// Actions
actions.resetSelectedRows = 'resetSelectedRows';
actions.toggleAllRowsSelected = 'toggleAllRowsSelected';
actions.toggleRowSelected = 'toggleRowSelected';

const useRowSelect = (hooks: any) => {
  hooks.getToggleRowSelectedProps = [defaultGetToggleRowSelectedProps];
  hooks.getToggleAllRowsSelectedProps = [defaultGetToggleAllRowsSelectedProps];
  hooks.stateReducers.push(reducer);
  hooks.useInstance.push(useInstance);
  hooks.prepareRow.push(prepareRow);
};

useRowSelect.pluginName = pluginName;

const defaultGetToggleRowSelectedProps = (props: any, { instance, row }: any) => {
  const { isRowSelectDisabled, manualRowSelectedKey = 'isSelected' } = instance;
  let checked = false;

  if (row.original && row.original[manualRowSelectedKey]) {
    checked = true;
  } else {
    checked = row.isSelected;
  }

  return [
    props,
    {
      onChange: (e: any) => {
        row.toggleRowSelected(e.target.checked);
      },
      style: {
        cursor: 'pointer'
      },
      checked,
      title: 'Toggle Row Selected',
      indeterminate: row.isSomeSelected,
      disabled: isRowSelectDisabled(row)
    }
  ];
};

const defaultGetToggleAllRowsSelectedProps = (props: any, { instance }: any) => [
  props,
  {
    onChange: (e: any) => {
      instance.toggleAllRowsSelected(e.target.checked);
    },
    style: {
      cursor: 'pointer'
    },
    checked: instance.isAllRowsSelected,
    title: 'Toggle All Rows Selected',
    indeterminate: Boolean(
      !instance.isAllRowsSelected && Object.keys(instance.state.selectedRowIds).length
    ),
    disabled: !instance.rows.some((row: any) => !instance.isRowSelectDisabled(row))
  }
];

const reducer = (state: any, action: any, previousState: any, instance: any) => {
  if (action.type === actions.init) {
    return {
      selectedRowIds: {},
      ...state
    };
  }

  if (action.type === actions.resetSelectedRows) {
    return {
      ...state,
      selectedRowIds: instance.initialState.selectedRowIds || {}
    };
  }

  if (action.type === actions.toggleAllRowsSelected) {
    const { value: setSelected } = action;
    const {
      isAllRowsSelected,
      isRowSelectDisabled,
      rowsById,
      nonGroupedRowsById = rowsById
    } = instance;

    const selectAll = typeof setSelected !== 'undefined' ? setSelected : !isAllRowsSelected;

    // Only remove/add the rows that are visible on the screen
    // Leave all the other rows that are selected alone.
    const selectedRowIds = Object.assign({}, state.selectedRowIds);

    if (selectAll) {
      Object.keys(nonGroupedRowsById).forEach((rowId) => {
        if (!isRowSelectDisabled(rowsById[rowId])) {
          selectedRowIds[rowId] = true;
        }
      });
    } else {
      Object.keys(nonGroupedRowsById).forEach((rowId) => {
        if (!isRowSelectDisabled(rowsById[rowId])) {
          delete selectedRowIds[rowId];
        }
      });
    }

    return {
      ...state,
      selectedRowIds
    };
  }

  if (action.type === actions.toggleRowSelected) {
    const { id, value: setSelected } = action;
    const { rowsById, selectSubRows = true } = instance;

    // Join the ids of deep rows
    // to make a key, then manage all of the keys
    // in a flat object
    const row = rowsById[id];
    const isSelected = row.isSelected;
    const shouldExist = typeof setSelected !== 'undefined' ? setSelected : !isSelected;

    if (isSelected === shouldExist) {
      return state;
    }

    const newSelectedRowIds = { ...state.selectedRowIds };

    const handleRowById = (id: any) => {
      const row = rowsById[id];

      if (!row.isGrouped) {
        if (shouldExist) {
          newSelectedRowIds[id] = true;
        } else {
          delete newSelectedRowIds[id];
        }
      }

      if (selectSubRows && row.subRows) {
        return row.subRows.forEach((row: any) => handleRowById(row.id));
      }
    };

    const getAllParentId = (id: any) => {
      const array = id.split('.');
      const parents = [];
      while (array && array.length > 1) {
        array.pop();
        parents.push(array.join('.'));
      }
      return parents;
    };

    const parentIds = getAllParentId(id);
    if (!shouldExist) {
      parentIds.forEach((i) => delete newSelectedRowIds[i]);
    }

    handleRowById(id);

    return {
      ...state,
      selectedRowIds: newSelectedRowIds
    };
  }
};

const useInstance = (instance: any) => {
  const {
    data,
    rows,
    getHooks,
    plugins,
    rowsById,
    nonGroupedRowsById = rowsById,
    autoResetSelectedRows = true,
    state: { selectedRowIds },
    selectSubRows = true,
    dispatch
  } = instance;

  ensurePluginOrder(plugins, ['useFilters', 'useGroupBy', 'useSortBy'], 'useRowSelect');

  const selectedFlatRows = React.useMemo(() => {
    const selectedFlatRows: any = [];

    rows.forEach((row: any) => {
      const isSelected = selectSubRows
        ? getRowIsSelected(row, selectedRowIds)
        : !!selectedRowIds[row.id];
      row.isSelected = !!isSelected;
      row.isSomeSelected = isSelected === null;

      if (isSelected) {
        selectedFlatRows.push(row);
      }
    });
    return selectedFlatRows;
  }, [rows, selectSubRows, selectedRowIds]);

  const getInstance = useGetLatest(instance);

  const isRowSelectDisabled = getHooks().isRowSelectDisabled;

  let isAllRowsSelected = Boolean(
    Object.keys(nonGroupedRowsById).length && Object.keys(selectedRowIds).length
  );

  if (isAllRowsSelected) {
    if (
      Object.keys(nonGroupedRowsById).some(
        (id) => !isRowSelectDisabled(rowsById[id]) && !selectedRowIds[id]
      )
    ) {
      isAllRowsSelected = false;
    }
  }

  const getAutoResetSelectedRows = useGetLatest(autoResetSelectedRows);

  useMountedLayoutEffect(() => {
    if (getAutoResetSelectedRows()) {
      dispatch({ type: actions.resetSelectedRows });
    }
  }, [dispatch, data]);

  const toggleAllRowsSelected = React.useCallback(
    (value: any) => dispatch({ type: actions.toggleAllRowsSelected, value }),
    [dispatch]
  );

  const toggleRowSelected = React.useCallback(
    (id: any, value: any) => dispatch({ type: actions.toggleRowSelected, id, value }),
    [dispatch]
  );
  const getToggleAllRowsSelectedProps = makePropGetter(getHooks().getToggleAllRowsSelectedProps, {
    instance: getInstance()
  });

  Object.assign(instance, {
    selectedFlatRows,
    isAllRowsSelected,
    isRowSelectDisabled,
    toggleRowSelected,
    toggleAllRowsSelected,
    getToggleAllRowsSelectedProps
  });
};

const prepareRow = (row: any, { instance }: any) => {
  row.toggleRowSelected = (set: any) => instance.toggleRowSelected(row.id, set);

  row.getToggleRowSelectedProps = makePropGetter(instance.getHooks().getToggleRowSelectedProps, {
    instance: instance,
    row
  });
};

const getRowIsSelected = (row: any, selectedRowIds: any) => {
  if (selectedRowIds[row.id]) {
    return true;
  }

  if (row.subRows && row.subRows.length) {
    let allChildrenSelected = true;
    let someSelected = false;

    row.subRows.forEach((subRow: any) => {
      // Bail out early if we know both of these
      if (someSelected && !allChildrenSelected) {
        return;
      }

      if (getRowIsSelected(subRow, selectedRowIds)) {
        someSelected = true;
      } else {
        allChildrenSelected = false;
      }
    });
    return allChildrenSelected ? true : someSelected ? null : false;
  }

  return false;
};

export default useRowSelect;
